import Link from 'next/link';
import { Block, Button, ButtonLink, createComponent, Flex, For, IconFA, If, IntrinsicProps, Loader, Slot, Title, toClassName, useClickOutside } from 'react-commons';
import { faChevronRight, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import style from './index.module.scss';
import { useAppStore } from '@/lib/stores/app';
import useSWR from 'swr';
import DailyGamesModel from '@/lib/drupal/models/DailyGames';
import Image from 'next/image';
import PerDevice from '../PerDevice';
import { useAuthStore } from '@/lib/drupal/stores/auth';
import { useAuthMethods } from '@/lib/hooks/useAuthMethods';
import { useSwitchLayout } from '@/lib/stores/theme';
import PromoAppBanner from '@/components/PromoAppBanner';
import { useFetchAnnualPlanPromo } from '@/lib/util/useAnnualPlanPromo';
import { CaptureClickEvent } from '@/lib/util/captureClickEvent';

interface DailySidebarMenuProps extends IntrinsicProps {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}

const classStates = [
  'isOpen'
];

export default createComponent<DailySidebarMenuProps>('DailySidebarMenu', { style, classStates }, function DailySidebarMenu ({ className }, props) {

  const router = useRouter();
  const [ appState ] = useAppStore();
  const [ authState ] = useAuthStore();
  const { logout } = useAuthMethods();

  const onlineGenres = appState.taxonomies.onlineGenres;
  const downloadGenres = appState.taxonomies.downloadGenres;

  const [ isOpen, setIsOpen ] = useState(false);

  // If page navigation occurs, close the menu
  useEffect(() => {
    const closeMenu = () => props.setIsOpen(false);
    router.events.on('routeChangeStart', closeMenu);

    return () => router.events.off('routeChangeStart', closeMenu);
  }, [ router.events, props ]);

  // Close when click outside
  const sidebarRef = useRef(null);
  useClickOutside({
    ref: sidebarRef,
    excludeRefs: [ { matches: '.DailyAppHeader__ToggleSidebar' } ]
  }, () => {
    props.setIsOpen(false);
  });

  const switchLayout = useSwitchLayout();

  const { data, isLoading } = useSWR([ 9, 0 ], ([ limit, offset ]) => DailyGamesModel.getFeaturedGames(limit, offset));

  const onSearch = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    if (evt.key === 'Enter') {
      const query = (evt.target as HTMLInputElement).value;
      router.push({
        pathname: '/search/all-games',
        query: {
          title: query,
        },
      });
    }
  };

  useFetchAnnualPlanPromo();

  return (
    <div ref={sidebarRef} className={className}>
      <Flex directionColumn expand wide tall>

        <div className='DailySidebarMenu__SearchInput'>
          <IconFA icon={faMagnifyingGlass} />
          <input type='text' placeholder='Search' onKeyUp={onSearch} />
        </div>
        
        <PromoAppBanner 
          eventAction='upsell_sale'
          eventTagName='upsell_sale_sidebar_menu'
        />

        <Flex directionColumn fit className='DailySidebarMenu__ScrollContainer'>

          <Block>

            {
              If(isLoading, () => (
                <Loader />
              )).Else(() => (
                <div className='LinkList'>
                  {
                    For(data, (item, i) => (
                      <Link href={item.href}>
                        <a className='Flex Flex--directionRow Flex--alignCenter'>
                          <figure>
                            <Image 
                              src={item.mobileIcon} 
                              alt={item.title + ' icon'}
                              layout='fill'
                            />
                          </figure>
                          <span>{item.title}</span>
                        </a>
                      </Link>
                    ))
                  }
                </div>
              )).EndIf()
            }

          </Block>

          <Block>

            <Title h4 size6>Online Games</Title>

            <div className='LinkList'>
              <Link href='/online/all-games'>
                <a>All Online Games</a>
              </Link>

              <Link href='/online/daily-games'>
                <a>Daily Games</a>
              </Link>
              {
                For(onlineGenres.filter((genre) => genre.uid !== '31'), (genre, i) => (
                  <Link href={genre.href} key={`online-${genre.uid}`}>
                    <a>{genre.name} Games</a>
                  </Link>
                ))
              }
            </div>

          </Block>

          <PerDevice className='Block'>
            <Slot name='desktop'>
          
              <Title h4 size6 className={isOpen ? '' : '--noMargin'}>
                <Flex alignCenter onClick={() => setIsOpen(!isOpen)} className='--isInteractive'>
                  <IconFA icon={faChevronRight} className={toClassName('DownloadAccordianCaret', { isOpen })} />
                  <span>Downloadable Games</span>
                </Flex>
              </Title>

              <div className={`LinkList ${isOpen ? '' : 'LinkList--collapsed'}`}>
                <Link href='/download/all-games'><a>All downloadable games</a></Link>
                {
                  For(downloadGenres, (genre, i) => (
                    <Link href={genre.href} key={`download-${genre.uid}`}><a>{genre.name} Games</a></Link>
                  ))
                }
              </div>


            </Slot>
          </PerDevice>

          <Block>

            <Title h4 size6>Learn More</Title>

            <div className='LinkList'>
              <Link href='/help'>FAQ</Link>
              <Link href='/about'>About Us</Link>
              {/* <Link href='https://supercalifragilisticexpialidocious.com/' target='_blank'>Corporate Site</Link> */}
              <a href='https://shockwavehelp.zendesk.com/hc/en-us/requests/new' target='_blank' rel='noreferrer'>Contact Us</a>
              <Link href='/about/terms'>Terms of Use</Link>
              <Link href='/about/privacyPolicy'>Privacy Policy</Link>
            </div>

          </Block>

          <Block className='--hideMobile'>
            <Title h4 size6 className='DailySidebarMenu__ThemeSwitcher'>
              <span onClick={() => {
                props.setIsOpen(false);
                switchLayout('classic');
              }}>Use Classic Layout</span>
            </Title>
          </Block>

          <Block>

            <Title h4 size6>Socials</Title>

            <div className='LinkList'>
              <Link href='https://x.com/Shockwavedotcom' target='_blank'>Twitter</Link>
              <Link href='https://www.youtube.com/@ShockwaveGames_' target='_blank'>YouTube</Link>
              <Link href='https://shop.shockwave.com' target='_blank'>Merchandise</Link>
            </div>
            
          </Block>

          <Block>
            <p className='Copyright'><strong>Copyright © {new Date().getFullYear()} Shockwave Games, Inc; all rights reserved.</strong></p>

            <p className='AboutUs'><Link href="/" target="_blank">Shockwave.com</Link> is the ultimate destination for free <Link href="/online/all-games" target="_blank">online games</Link>, free <Link href="/download/all-games" target="_blank">download games</Link>, and more! This is your chance to play games 24-7, with a ton of great categories to choose from - <Link href="/online/action-games" target="_blank">Action Games</Link>, <Link href="/online/adventure-games" target="_blank">Adventure Games</Link>, <Link href="/online/card-board-games" target="_blank">Card & Board Games</Link>, <Link href="/online/jigsaws-games" target="_blank">Jigsaw Puzzle Games</Link>, <Link href="/online/family-kids-games" target="_blank">Family & Kids Games</Link>, <Link href="/online/music-photos-games" target="_blank">Music & Photo Games</Link>, <Link href="/online/puzzle-games" target="_blank">Puzzle Games</Link>, <Link href="/online/racing-games" target="_blank">Racing Games</Link>, <Link href="/online/shooters-games" target="_blank">Shooting Games</Link>, <Link href="/online/sports-games" target="_blank">Sports Games</Link>, <Link href="/online/strategy-games" target="_blank">Strategy Games</Link>, <Link href="/online/word-games" target="_blank">Word Games</Link>, and more. Best of all, <Link href="/" target="_blank">Shockwave.com</Link> is updated daily, making our library of games virtually endless! If you're a gamer looking for some serious fun, join <Link href="/unlimited">Shockwave® UNLIMITED</Link> and receive access to exclusive online games and downloads for a low monthly fee. You'll enjoy unlimited play on all download games, no ad interruptions on all online games and brand new games each week. Shockwave.com has games for everyone - free online games, free download games and new daily games each week, not to mention game reviews and ratings, a community full of gamers and plenty of exclusive member perks. Looking for more games? Shockwave has a large selection of fun online and download games. What are you waiting for? Game on! Part of the Shockwave network.</p>
          </Block>

        </Flex>

        <Flex alignCenter justifySpaceEvenly className='DailySidebarMenu__Footer --padMd0'>

          {
            If(!authState?.ready, () => (
              <>
                <Loader />
              </>
            )).ElseIf(!authState?.user, () => (
              <>
                <CaptureClickEvent
                  action='upsell'
                  location='daily_sidebar_menu'
                  properties={() => ({
                    tag_name: 'upsell_sidebar',
                  })}
                  href='/unlimited'
                >
                  <ButtonLink fullWidth rounded medium secondary href='/unlimited' className='upsell_sidebar'>Subscribe</ButtonLink>
                </CaptureClickEvent>
                <ButtonLink fullWidth rounded medium secondary href='/account/login'>Log in</ButtonLink>
              </>
            )).Else(() => (
              <>
                {
                  If(!authState.user.isPremiumUser, () => (
                    <CaptureClickEvent
                      action='upsell'
                      location='daily_sidebar_menu'
                      className='upsell_sidebar_2'
                      properties={() => ({
                        tag_name: 'upsell_sidebar',
                      })}
                      href='/unlimited'
                    >
                      <ButtonLink fullWidth rounded medium secondary href='/unlimited' className='upsell_sidebar_2'>Subscribe</ButtonLink>
                    </CaptureClickEvent>
                  )).EndIf()
                }
                <Button onClick={logout} fullWidth rounded medium secondary>Log out</Button>
              </>
            )).EndIf()
          }

        </Flex>

      </Flex>
    </div>
  );
});
